/*
 Copyright 2019-present wobe-systems GmbH

 Licensed under the Apache License, Version 2.0 (the 'License');
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

     http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an 'AS IS' BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.
*/
import { ActionTree } from 'vuex';
import * as types from '@/store/types';
import { handleErrorMessage } from '@/store/handleCatchError';
import { logging } from '@/scripts/debugger';
import { RootState } from '../models';
import { BrickInPackage, PackageState } from './models';
import { GetPackageBricks, GetPackages } from './apis';

const log = (message: string) => {
  logging('VUEX/packages/actions', message);
};

const service : string = 'Brick Store';

export const actions: ActionTree<PackageState, RootState> = {
  /**
   * Fetches all the packages available in the
   * brick store
   *
   */
  async [types.FETCH_ALL_PACKAGES]({ commit, dispatch }): Promise<any> {
    commit(types.LOADING, true);

    return new Promise((resolve, reject) => {
      GetPackages()
        .then(({ data }) => {
          const packages: PackageState['packages'] = data;

          commit(types.MUTATE_ALL_PACKAGES, packages);
          commit(types.LOADING, false);
          resolve(data);
        })
        .catch((err) => {
          commit(types.LOADING, false);
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },

  /**
   * Fetches details of a single package with a list
   * of all the bricks inside the package
   *
   * @param id The ID of the package to fetch
   */
  async [types.FETCH_PACKAGE_BRICKS](
    { commit, dispatch }, { id }: { id: string},
  ): Promise<any> {
    await GetPackageBricks(id)
      .then(({ data }) => {
        const bricks: BrickInPackage[] = data;

        commit(types.MUTATE_BRICKS_OF_PACKAGE, bricks);
      })
      .catch((err) => handleErrorMessage(err, dispatch, service));
  },
};
